import { useState, useEffect } from "react";
import {
    ClothesTypes,
    ClothesSize,
    ClothesColors,
    ClothesGender,
} from "../models/clothes/clothes.model";
import { categoryLabels, categoryList } from "../utils/categoryData";
import { sizeOptions, colorOptions, colorLabels, genderOptions } from "../utils/filterOptions";
import { FaTimes } from "react-icons/fa";

export interface ClothesFiltersState {
    category: ClothesTypes | null;
    gender: ClothesGender | null;
    size: ClothesSize | null;
    colors: ClothesColors[];
    minSellPrice: string;
    maxSellPrice: string;
    minRentPrice: string;
    maxRentPrice: string;
}

export const defaultFilters: ClothesFiltersState = {
    category: null,
    gender: null,
    size: null,
    colors: [],
    minSellPrice: "0",
    maxSellPrice: "500",
    minRentPrice: "0",
    maxRentPrice: "100",
};

interface ClothesFiltersModalProps {
    visible: boolean;
    filters: ClothesFiltersState;
    onConfirm: (filters: ClothesFiltersState) => void;
    onCancel: () => void;
}

const ClothesFiltersModal = ({
    visible,
    filters,
    onConfirm,
    onCancel,
}: ClothesFiltersModalProps) => {
    const [local, setLocal] = useState<ClothesFiltersState>(filters);

    useEffect(() => {
        if (visible) setLocal(filters);
    }, [visible, filters]);

    const update = (patch: Partial<ClothesFiltersState>) => {
        setLocal((prev) => ({ ...prev, ...patch }));
    };

    const toggleColor = (c: ClothesColors) => {
        setLocal((prev) => ({
            ...prev,
            colors: prev.colors.includes(c) ? prev.colors.filter((x) => x !== c) : [...prev.colors, c],
        }));
    };

    const handleConfirm = () => {
        onConfirm(local);
        onCancel();
    };

    const handleClear = () => {
        setLocal(defaultFilters);
    };

    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={onCancel} aria-hidden="true" />
            <div className="relative bg-white w-full max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col max-w-lg">
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-semibold text-[#704e37]">Filtro</h2>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="p-2 rounded-full hover:bg-gray-100"
                        aria-label="Mbyll"
                    >
                        <FaTimes className="text-xl text-gray-600" />
                    </button>
                </div>
                <div className="overflow-y-auto flex-1 p-4 space-y-5">
                    {/* Category */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Kategoria</label>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => update({ category: null })}
                                className={`px-3 py-1.5 rounded-lg text-sm ${local.category === null ? "bg-[#704e37] text-white" : "bg-gray-100 text-gray-700"}`}
                            >
                                Të gjitha
                            </button>
                            {categoryList.map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => update({ category: t })}
                                    className={`px-3 py-1.5 rounded-lg text-sm ${local.category === t ? "bg-[#704e37] text-white" : "bg-gray-100 text-gray-700"}`}
                                >
                                    {categoryLabels[t]}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Gender */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gjinia</label>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => update({ gender: null })}
                                className={`px-3 py-1.5 rounded-lg text-sm ${local.gender === null ? "bg-[#704e37] text-white" : "bg-gray-100 text-gray-700"}`}
                            >
                                Të gjitha
                            </button>
                            {genderOptions.map(({ value, label }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => update({ gender: value })}
                                    className={`px-3 py-1.5 rounded-lg text-sm ${local.gender === value ? "bg-[#704e37] text-white" : "bg-gray-100 text-gray-700"}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Size */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Madhësia</label>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => update({ size: null })}
                                className={`px-3 py-1.5 rounded-lg text-sm ${local.size === null ? "bg-[#704e37] text-white" : "bg-gray-100 text-gray-700"}`}
                            >
                                Të gjitha
                            </button>
                            {sizeOptions.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => update({ size: local.size === s ? null : s })}
                                    className={`px-3 py-1.5 rounded-lg text-sm ${local.size === s ? "bg-[#704e37] text-white" : "bg-gray-100 text-gray-700"}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Colors */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Ngjyra</label>
                        <div className="flex flex-wrap gap-2">
                            {colorOptions.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => toggleColor(c)}
                                    className={`px-3 py-1.5 rounded-lg text-sm ${local.colors.includes(c) ? "bg-[#704e37] text-white" : "bg-gray-100 text-gray-700"}`}
                                >
                                    {colorLabels[c]}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Sell price */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Çmimi i shitjes (€)</label>
                        <div className="flex gap-2 items-center">
                            <input
                                type="number"
                                min={0}
                                placeholder="Min"
                                value={local.minSellPrice}
                                onChange={(e) => update({ minSellPrice: e.target.value })}
                                className="w-24 border rounded px-2 py-1.5 text-sm"
                            />
                            <span className="text-gray-500">–</span>
                            <input
                                type="number"
                                min={0}
                                placeholder="Max"
                                value={local.maxSellPrice}
                                onChange={(e) => update({ maxSellPrice: e.target.value })}
                                className="w-24 border rounded px-2 py-1.5 text-sm"
                            />
                        </div>
                    </div>

                    {/* Rent price */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Çmimi i qirasë (€)</label>
                        <div className="flex gap-2 items-center">
                            <input
                                type="number"
                                min={0}
                                placeholder="Min"
                                value={local.minRentPrice}
                                onChange={(e) => update({ minRentPrice: e.target.value })}
                                className="w-24 border rounded px-2 py-1.5 text-sm"
                            />
                            <span className="text-gray-500">–</span>
                            <input
                                type="number"
                                min={0}
                                placeholder="Max"
                                value={local.maxRentPrice}
                                onChange={(e) => update({ maxRentPrice: e.target.value })}
                                className="w-24 border rounded px-2 py-1.5 text-sm"
                            />
                        </div>
                    </div>
                </div>
                <div className="p-4 border-t flex gap-2">
                    <button
                        type="button"
                        onClick={handleClear}
                        className="flex-1 py-2.5 rounded-lg border border-[#704e37] text-[#704e37] font-medium"
                    >
                        Pastro
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        className="flex-1 py-2.5 rounded-lg bg-[#704e37] text-white font-medium"
                    >
                        Konfirmo
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ClothesFiltersModal;
